// The change lines of an Operators audit entry (computed by the feed read, see
// api2/src/modules/operators/operator-audit-describer.ts) as the entry renders them:
// "Peaksolution · Rolle: Customer Expert → Customer Admin", "MyServices · Tags: + Bergbahn, − Gastro".
// Pure: translations come in through `label(key, englishFallback)`.

// bare translation key with an English fallback while the translation row is missing;
// components bind it to themselves: `label(this, key, fallback)`
export function translatedLabel(vm, key, fallback) {
	return vm.$te(`text.${ key }`) ? vm.$t(`text.${ key }`) : fallback
}

// English text for every event (translation key operatorAuditEvent_<event>), used until its
// translation row exists. Also the event filter's options, in this order.
export const EVENT_FALLBACKS = {
	created: 'Created', deleted: 'Deleted', permissionsChanged: 'Permissions changed', enabled: 'Enabled', disabled: 'Disabled',
	twoFactorEnabled: '2FA enabled', twoFactorDisabled: '2FA disabled', recoveryEmailSent: 'Password e-mail sent',
	markedForDeletion: 'Marked for deletion', partnerAccessRemoved: 'Partner access removed', provisioningRetried: 'Provisioning retried',
	peaksolutionLoginOnBehalf: 'Peaksolution login on behalf', other: 'Changed',
}

const EVENT_COLORS ={ deleted: 'red', disabled: 'red', created: 'green', enabled: 'green' }

export function eventColor(event) {
	return EVENT_COLORS[event]
}

// the account first, then the dialog's four cards in their order
const MODULE_ORDER = [ 'account', 'myServices', 'contentHub', 'reportCenter', 'peaksolution' ]

// product names are not translated
const MODULE_NAMES = { myServices: 'MyServices', contentHub: 'Content Hub', reportCenter: 'Report Center', peaksolution: 'Peaksolution' }
const SYSTEM_NAMES = { ...MODULE_NAMES, metis: 'Metis', flexiblePricing: 'Flexible Pricing (PS3)', keycloak: 'Keycloak', contentManager: 'Content Manager' }

// field → [ translation key, English fallback ]; the dialog's own keys where it has one
const FIELD_LABELS = {
	applications: [ 'applications', 'Applications' ],
	tags: [ 'tags', 'Tags' ],
	role: [ 'operatorAuditField_role', 'Role' ],
	'scope.salesChannels': [ 'salesChannels', 'Sales channels' ],
	'scope.regions': [ 'regions', 'Regions' ],
	'scope.serviceProviders': [ 'serviceProviders', 'Service providers' ],
	'scope.products': [ 'products', 'Products' ],
	'scope.resellers': [ 'resellers', 'Resellers' ],
	twoFactorAuth: [ 'operatorAuditField_twoFactorAuth', '2FA' ],
	markedForDeletion: [ 'operatorAuditField_markedForDeletion', 'Marked for deletion' ],
	name: [ 'name', 'Name' ],
	email: [ 'loginEmail', 'Login e-mail' ],
	deprovision: [ 'operatorAuditField_deprovision', 'Access removal' ],
	provisioningRetry: [ 'operatorAuditField_provisioningRetry', 'Provisioning retry' ],
	loginOnBehalf: [ 'operatorAuditField_loginOnBehalf', 'Logged in by' ],
}

// fields that record a fact rather than a change: only the known side is shown
const ONE_SIDED_FIELDS = new Set([ 'name', 'email', 'loginOnBehalf' ])

function moduleName(module, label) {
	return module === 'account' ? label('operatorAuditModule_account', 'Account') : (MODULE_NAMES[module] ?? module)
}

// switching a whole module on or off reads "Content Hub: off → on" — no field label
function fieldName(line, label) {
	if (line.field === 'enabled') return line.module === 'account' ? label('operatorAuditField_accountEnabled', 'Active') : null
	const known = FIELD_LABELS[line.field]
	return known ? label(...known) : line.field
}

function displayValue(value, label) {
	if (value === undefined || value === null) return '–'
	if (value === true) return label('operatorAuditValue_on', 'on')
	if (value === false) return label('operatorAuditValue_off', 'off')
	return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

// the Peaksolution role is stored as its PEAK role id: named from the loaded Peak roles, else the id
function roleName(value, peakRoles) {
	if (value === undefined || value === null) return value
	return peakRoles.find(role => String(role.id) === String(value))?.name ?? value
}

function valueText(line, { label, dimensionValues = {}, peakRoles = [] }) {
	if (line.added || line.removed) {
		const scopeKey = line.field.startsWith('scope.') ? line.field.slice('scope.'.length) : null
		const name = id => (scopeKey && dimensionValues[scopeKey]?.find(item => item.id === id)?.label) || id
		const parts = []
		if (line.added?.length) parts.push(`+ ${ line.added.map(name).join(', ') }`)
		if (line.removed?.length) parts.push(`− ${ line.removed.map(name).join(', ') }`)
		return parts.join(', ')
	}
	if (line.system) {
		const status = line.status ? ` ${ label(`operatorAuditStatus_${ line.status }`, line.status) }` : ''
		return `${ SYSTEM_NAMES[line.system] ?? line.system }${ status }${ line.error ? ` (${ line.error })` : '' }`
	}
	if (ONE_SIDED_FIELDS.has(line.field)) return displayValue(line.after ?? line.before, label)
	if (line.module === 'peaksolution' && line.field === 'role') {
		return `${ displayValue(roleName(line.before, peakRoles), label) } → ${ displayValue(roleName(line.after, peakRoles), label) }`
	}
	return `${ displayValue(line.before, label) } → ${ displayValue(line.after, label) }`
}

// [{ module, lines: [{ ...line, text }] }] in display order
export function changeLineGroups(changes, options) {
	const groups = []
	for (const line of changes ?? []) {
		let group = groups.find(g => g.module === line.module)
		if (!group) groups.push(group = { module: line.module, lines: [] })
		const field = fieldName(line, options.label)
		const prefix = moduleName(line.module, options.label) + (field ? ` · ${ field }` : '')
		group.lines.push({ ...line, text: `${ prefix }: ${ valueText(line, options) }` })
	}
	const rank = module => {
		const index = MODULE_ORDER.indexOf(module)
		return index === -1 ? MODULE_ORDER.length : index
	}
	return groups.sort((a, b) => rank(a.module) - rank(b.module))
}
